import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { extractRefreshTokenFromUnknown } from '../api/authRefresh';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyHttpFailureSummary,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { devProxyRouteForApiPath, fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { supportsOtpLiteRegister } from './authEntryPolicy';
import type { LoginOption } from './loginConstants';

export type EmailOtpApiResult = {
  ok: boolean;
  status: number;
  parsed: unknown;
  responseText: string;
  urlUsed: string;
  response: Response;
  accessToken: string | null;
  refreshToken: string | null;
  pathUsed: string;
};

function pathCandidates(configured: string | undefined, defaults: readonly string[]): string[] {
  const paths = [configured?.trim(), ...defaults].filter((value): value is string => Boolean(value?.trim()));
  return Array.from(new Set(paths));
}

function otpLoginSendPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_OTP_SEND_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['login/clients/otp/send', 'login/PrivateClient/otp/send'],
    'business client': ['login/BusinessClient/otp/send', 'login/clients/otp/send'],
    'business worker': ['login/BusinessWorker/otp/send', 'login/clients/otp/send'],
    'asset owner': ['login/AssetOwner/otp/send', 'login/assetsowners/otp/send'],
    'business asset owner': ['login/AssetOwner/otp/send', 'login/BusinessAssetOwner/otp/send', 'login/assetsowners/otp/send'],
    'private asset owner': ['login/AssetOwner/otp/send', 'login/PrivateAssetOwner/otp/send', 'login/assetsowners/otp/send'],
  };
  return pathCandidates(configured, byRole[loginType] ?? ['login/clients/otp/send']);
}

function otpRegisterSendPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_OTP_REGISTER_SEND_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['register/PrivateClient/otp/send'],
    'business client': ['register/BusinessClient/otp/send'],
    'business worker': ['register/BusinessWorker/otp/send'],
    'asset owner': ['register/AssetOwner/otp/send'],
    'business asset owner': ['register/AssetOwner/otp/send', 'register/BusinessAssetOwner/otp/send'],
    'private asset owner': ['register/AssetOwner/otp/send', 'register/PrivateAssetOwner/otp/send'],
  };
  return pathCandidates(configured, byRole[loginType] ?? []);
}

function otpLoginVerifyPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_OTP_VERIFY_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['login/clients/otp/verify', 'login/PrivateClient/otp/verify'],
    'business client': ['login/BusinessClient/otp/verify', 'login/clients/otp/verify'],
    'business worker': ['login/BusinessWorker/otp/verify', 'login/clients/otp/verify'],
    'asset owner': ['login/AssetOwner/otp/verify', 'login/assetsowners/otp/verify'],
    'business asset owner': ['login/AssetOwner/otp/verify', 'login/BusinessAssetOwner/otp/verify', 'login/assetsowners/otp/verify'],
    'private asset owner': ['login/AssetOwner/otp/verify', 'login/PrivateAssetOwner/otp/verify', 'login/assetsowners/otp/verify'],
  };
  return pathCandidates(configured, byRole[loginType] ?? ['login/clients/otp/verify']);
}

function otpRegisterVerifyPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_OTP_REGISTER_VERIFY_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['register/PrivateClient/otp/verify'],
    'business client': ['register/BusinessClient/otp/verify'],
    'business worker': ['register/BusinessWorker/otp/verify'],
    'asset owner': ['register/AssetOwner/otp/verify'],
    'business asset owner': ['register/AssetOwner/otp/verify', 'register/BusinessAssetOwner/otp/verify'],
    'private asset owner': ['register/AssetOwner/otp/verify', 'register/PrivateAssetOwner/otp/verify'],
  };
  return pathCandidates(configured, byRole[loginType] ?? []);
}

/** @internal tests */
export function otpLoginSendPathCandidatesForTests(loginType: LoginOption): string[] {
  return otpLoginSendPathCandidates(loginType);
}

/** @internal tests */
export function otpRegisterSendPathCandidatesForTests(loginType: LoginOption): string[] {
  return otpRegisterSendPathCandidates(loginType);
}

/** @internal tests */
export function otpLoginVerifyPathCandidatesForTests(loginType: LoginOption): string[] {
  return otpLoginVerifyPathCandidates(loginType);
}

/** @internal tests */
export function otpRegisterVerifyPathCandidatesForTests(loginType: LoginOption): string[] {
  return otpRegisterVerifyPathCandidates(loginType);
}

/** @internal tests */
export function otpSendPathsInOrderForTests(
  loginType: LoginOption,
): Array<{ path: string; route: 'api' | 'login' }> {
  const paths = [
    ...otpLoginSendPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? otpRegisterSendPathCandidates(loginType) : []),
  ];
  return paths.map((path) => ({ path, route: fetchRouteForPath(path) }));
}

/** @internal tests */
export function otpVerifyPathsInOrderForTests(
  loginType: LoginOption,
): Array<{ path: string; route: 'api' | 'login' }> {
  const paths = [
    ...otpLoginVerifyPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? otpRegisterVerifyPathCandidates(loginType) : []),
  ];
  return paths.map((path) => ({ path, route: fetchRouteForPath(path) }));
}

/** @deprecated Use otpLoginSendPathCandidatesForTests — kept for existing imports. */
export const EMAIL_OTP_SEND_PATHS = otpLoginSendPathCandidates('private client');

/** @deprecated Use otpLoginVerifyPathCandidatesForTests — kept for existing imports. */
export const EMAIL_OTP_VERIFY_PATHS = otpLoginVerifyPathCandidates('private client');

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailAddress(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 5 && EMAIL_PATTERN.test(trimmed);
}

/**
 * Normalize a typed/pasted OTP. Strips spaces; if the clipboard has SMS prose
 * (“Your code is 123456”), keep the longest 4–8 digit run.
 */
export function normalizeOtpCode(value: string): string {
  const compact = value.replace(/\s+/g, '').trim();
  if (!compact) {
    return '';
  }
  if (/^\d{4,8}$/.test(compact)) {
    return compact;
  }
  const digitRuns = value.match(/\d{4,8}/g);
  if (digitRuns && digitRuns.length > 0) {
    return digitRuns.reduce((best, run) => (run.length >= best.length ? run : best), digitRuns[0] ?? '');
  }
  // Allow partial numeric typing (user still entering digits).
  const digitsOnly = compact.replace(/\D/g, '');
  return digitsOnly.slice(0, 8);
}

export function otpApiErrorMessage(responseText: string): string {
  if (!responseText.trim()) {
    return '';
  }
  try {
    const parsed = JSON.parse(responseText) as { message?: unknown };
    const message = parsed?.message;
    if (typeof message === 'string') {
      return message;
    }
    if (Array.isArray(message)) {
      return message.filter((item): item is string => typeof item === 'string').join('; ');
    }
  } catch {
    return responseText.trim();
  }
  return responseText.trim();
}

/** Dedicated login OTP routes (e.g. `login/clients/otp/send`). */
export function isDedicatedOtpApiPath(path: string): boolean {
  return /\/otp(\/|$)/.test(path);
}

/** Nest/Express “route not registered” 404 — safe to try the next candidate path. */
export function isOtpRouteMissingOnServer(status: number, responseText: string): boolean {
  return status === 404 && /cannot (post|get|put|patch|delete)/i.test(responseText);
}

export function isOtpUserMissingError(status: number, responseText: string): boolean {
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return false;
  }
  const message = otpApiErrorMessage(responseText).toLowerCase();
  if (status === 404 || status === 401) {
    return /user does not exist|user not found|account not found|no user|not registered/.test(message);
  }
  return false;
}

export function isOtpRegisterConflictError(status: number, responseText: string): boolean {
  if (status === 409) {
    return true;
  }
  const message = otpApiErrorMessage(responseText).toLowerCase();
  return /already exists|already registered|duplicate|conflict/.test(message);
}

function loginSendBodies(email: string): Record<string, unknown>[] {
  return [{ email, purpose: 'login' }, { email }];
}

function otpVerifyBodies(email: string, code: string): Record<string, unknown>[] {
  return [
    { email, code, purpose: 'login' },
    { email, code },
  ];
}

function shouldTryNextOtpLoginSendCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return true;
  }
  if (path.startsWith('login/') && isOtpUserMissingError(status, responseText)) {
    return true;
  }
  if (isDedicatedOtpApiPath(path)) {
    return false;
  }
  return status === 404 || status === 401;
}

function shouldTryNextOtpRegisterSendCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return true;
  }
  return false;
}

function shouldTryNextOtpLoginVerifyCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return true;
  }
  if (path.startsWith('login/') && isOtpUserMissingError(status, responseText)) {
    return true;
  }
  if (isDedicatedOtpApiPath(path)) {
    return false;
  }
  return status === 404 || status === 401;
}

function shouldTryNextOtpRegisterVerifyCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  return isOtpRouteMissingOnServer(status, responseText);
}

function recordOtpFailure(
  result: EmailOtpApiResult,
  body: Record<string, unknown>,
): { friendly: string; details: string | null } {
  return {
    friendly: friendlyHttpFailureSummary(result.status, '', result.responseText),
    details: formatHttpErrorDetails({
      requestUrl: result.urlUsed,
      requestPayload: body,
      response: result.response,
      responseText: result.responseText,
    }),
  };
}

/** Register OTP routes live on the main API (`/dev-api-proxy`), not the login service. */
export function fetchRouteForPath(path: string): 'api' | 'login' {
  return devProxyRouteForApiPath(path);
}

async function postJson(
  apiPathTail: string,
  body: Record<string, unknown>,
  route: 'api' | 'login',
): Promise<EmailOtpApiResult> {
  const { response, urlUsed } = await fetchWithDevProxyFallback(
    apiPathTail,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    route,
  );
  const responseText = await response.text();
  let parsed: unknown = null;
  if (responseText.trim()) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  const accessToken = parsed ? extractAccessTokenFromLoginJson(parsed) : null;
  const refreshToken = parsed ? extractRefreshTokenFromUnknown(parsed) : null;
  return {
    ok: isSuccessfulHttpStatus(response.status),
    status: response.status,
    parsed,
    responseText,
    urlUsed,
    response,
    accessToken,
    refreshToken,
    pathUsed: apiPathTail,
  };
}

type OtpSendAttempt = EmailOtpSendOutcome & { sawUserMissing?: boolean };

function otpSendPathsInOrder(loginType: LoginOption): string[] {
  return [
    ...otpLoginSendPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? otpRegisterSendPathCandidates(loginType) : []),
  ];
}

function otpVerifyPathsInOrder(loginType: LoginOption): string[] {
  return [
    ...otpLoginVerifyPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? otpRegisterVerifyPathCandidates(loginType) : []),
  ];
}

async function tryOtpSend(email: string, loginType: LoginOption): Promise<OtpSendAttempt> {
  const paths = otpSendPathsInOrder(loginType);
  let lastFriendly = 'Could not send a sign-in code.';
  let lastDetails: string | null = null;
  let sawLegacyUnauthorized = false;
  let sawUserMissing = false;
  const hasDedicatedOtpPath = paths.some(isDedicatedOtpApiPath);

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const hasMorePaths = index < paths.length - 1;
    for (const body of loginSendBodies(email)) {
      const result = await postJson(path, body, fetchRouteForPath(path));
      if (result.ok) {
        return { ok: true };
      }

      const failure = recordOtpFailure(result, body);
      lastFriendly = failure.friendly;
      lastDetails = failure.details;

      if (isOtpUserMissingError(result.status, result.responseText)) {
        sawUserMissing = true;
      }

      const tryNext = path.startsWith('register/')
        ? shouldTryNextOtpRegisterSendCandidate(path, result.status, result.responseText, hasMorePaths)
        : shouldTryNextOtpLoginSendCandidate(path, result.status, result.responseText, hasMorePaths);

      if (!tryNext) {
        return {
          ok: false,
          friendly: lastFriendly,
          details: lastDetails,
          unauthorized: false,
          sawUserMissing,
        };
      }
      if (!isDedicatedOtpApiPath(path) && result.status === 401) {
        sawLegacyUnauthorized = true;
      }
    }
  }

  return {
    ok: false,
    friendly: lastFriendly,
    details: lastDetails,
    unauthorized: sawLegacyUnauthorized && !hasDedicatedOtpPath,
    sawUserMissing,
  };
}

export type EmailOtpSendOutcome =
  | { ok: true }
  | { ok: false; friendly: string; details: string | null; unauthorized: boolean };

export async function sendEmailOtpCode(
  email: string,
  loginType: LoginOption = 'private client',
): Promise<EmailOtpSendOutcome> {
  const normalizedEmail = email.trim();

  try {
    const result = await tryOtpSend(normalizedEmail, loginType);
    if (result.ok) {
      return { ok: true };
    }
    return {
      ok: false,
      friendly: result.friendly,
      details: result.details,
      unauthorized: result.unauthorized,
    };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
      unauthorized: false,
    };
  }
}

async function tryOtpVerify(
  email: string,
  code: string,
  loginType: LoginOption,
): Promise<EmailOtpVerifyOutcome & { sawUserMissing?: boolean }> {
  const paths = otpVerifyPathsInOrder(loginType);
  let lastFriendly = 'Could not verify the sign-in code.';
  let lastDetails: string | null = null;
  let sawUserMissing = false;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const hasMorePaths = index < paths.length - 1;
    for (const body of otpVerifyBodies(email, code)) {
      const result = await postJson(path, body, fetchRouteForPath(path));
      if (result.ok && result.accessToken) {
        return {
          ok: true,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          parsed: result.parsed,
        };
      }
      if (result.ok) {
        continue;
      }
      const failure = recordOtpFailure(result, body);
      lastFriendly = failure.friendly;
      lastDetails = failure.details;
      if (isOtpUserMissingError(result.status, result.responseText)) {
        sawUserMissing = true;
      }

      const tryNext = path.startsWith('register/')
        ? shouldTryNextOtpRegisterVerifyCandidate(path, result.status, result.responseText, hasMorePaths)
        : shouldTryNextOtpLoginVerifyCandidate(path, result.status, result.responseText, hasMorePaths);

      if (!tryNext) {
        return { ok: false, friendly: lastFriendly, details: lastDetails, sawUserMissing };
      }
    }
  }

  return { ok: false, friendly: lastFriendly, details: lastDetails, sawUserMissing };
}

export type EmailOtpVerifyOutcome =
  | { ok: true; accessToken: string; refreshToken: string | null; parsed: unknown }
  | { ok: false; friendly: string; details: string | null };

export async function verifyEmailOtpCode(
  email: string,
  code: string,
  loginType: LoginOption,
): Promise<EmailOtpVerifyOutcome> {
  const normalizedEmail = email.trim();
  const normalizedCode = normalizeOtpCode(code);

  try {
    const firstPass = await tryOtpVerify(normalizedEmail, normalizedCode, loginType);
    if (firstPass.ok) {
      return firstPass;
    }

    const hasDedicatedVerifyPath = otpVerifyPathsInOrder(loginType).some(isDedicatedOtpApiPath);
    if (hasDedicatedVerifyPath) {
      return { ok: false, friendly: firstPass.friendly, details: firstPass.details };
    }

    const loginPath =
      loginType === 'private client' || loginType === 'business client' || loginType === 'business worker'
        ? 'login/clients'
        : 'login/assetsowners';
    const loginResult = await postJson(
      loginPath,
      { identifier: normalizedEmail, password: normalizedCode },
      'login',
    );
    if (loginResult.ok && loginResult.accessToken) {
      return {
        ok: true,
        accessToken: loginResult.accessToken,
        refreshToken: loginResult.refreshToken,
        parsed: loginResult.parsed,
      };
    }

    let lastFriendly = firstPass.friendly;
    let lastDetails = firstPass.details;
    if (!loginResult.ok) {
      lastFriendly = friendlyHttpFailureSummary(loginResult.status, '', loginResult.responseText);
      lastDetails = formatHttpErrorDetails({
        requestUrl: loginResult.urlUsed,
        requestPayload: { identifier: normalizedEmail, password: '***' },
        response: loginResult.response,
        responseText: loginResult.responseText,
      });
    }

    return { ok: false, friendly: lastFriendly, details: lastDetails };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
    };
  }
}

export function friendlyOtpSendFailureSummary(
  friendly: string,
  options?: { userNotRegistered?: string; notAvailable?: string; unauthorized?: boolean },
): string {
  if (options?.unauthorized && options.notAvailable) {
    return options.notAvailable;
  }
  if (/user does not exist|user not found|account not found|not registered/i.test(friendly)) {
    return options?.userNotRegistered ?? friendly;
  }
  return friendly;
}
